<script lang="ts">
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import Dialogue from "$components/+blocks/Dialogue/Dialogue.svelte";
  import TabButton from "./TabButton.svelte";
  import GutterMenu from "$components/editor/extensions/+shared/GutterMenu.svelte";
  import { tabBlocks } from "$components/editor/extensions/blocks/Tabs/tabs";
  import { v4 as uuidv4 } from "uuid";
  import { onMount, tick } from "svelte";
  import { NodeViewWrapper, NodeViewContent } from "svelte-tiptap";
  import { fly } from "svelte/transition";
  import type { NodeViewProps } from "@tiptap/core";
  import type { Tab } from "./TabsPanel";
  import { Left, Plus, Right } from "$components/+blocks/OVIcon";

  export let node: NodeViewProps["node"];
  export let editor: NodeViewProps["editor"];
  export let updateAttributes: NodeViewProps["updateAttributes"];
  export let deleteNode: NodeViewProps["deleteNode"];
  export let getPos: NodeViewProps["getPos"];
  export let isEditable: boolean;
  $$restProps; // consume unused tiptap props

  let tabsContainer: HTMLDivElement;
  let targetTab: { id: string; title: string; index: number } = {
    id: "",
    title: "",
    index: 0,
  };

  let isDeleteTabModalOpen = false;
  let isDeleteBlockModalOpen = false;

  let scrollPosition = 0;
  let isScrollable = false;
  let atMinScroll = false;
  let atMaxScroll = false;

  let observer: ResizeObserver;

  const blockId = node.attrs.id;
  let tabs = [...node.attrs.tabs];
  $: tabs = [...node.attrs.tabs];

  // if no active tab, set the first tab as active
  if (!$tabBlocks.get(blockId)?.activeTab && tabs?.length && tabs[0]) {
    tabBlocks.setActiveTab(blockId, tabs[0].id, "left");
  }

  function updateScrollStatus() {
    // check if the tabs container is scrollable
    // (if the total width (including overflow) is greater than the visible width)
    isScrollable = tabsContainer?.scrollWidth > tabsContainer?.clientWidth;

    // check if the scroll position is at the start of the container
    atMinScroll = tabsContainer?.scrollLeft === 0;

    // check if the scroll position is at the end of the container, with a tolerance of 2px
    atMaxScroll =
      tabsContainer?.scrollWidth - tabsContainer?.scrollLeft - 2 <= tabsContainer?.clientWidth;

    // get the current scroll position (distance from leftmost)
    scrollPosition = tabsContainer?.scrollLeft;
  }

  onMount(() => {
    // initialise scroll values
    updateScrollStatus();

    // watch for changes in the tabs container width to toggle visibility of the scroll button
    observer = new ResizeObserver(() => {
      updateScrollStatus();
    });

    observer.observe(tabsContainer);

    return () => observer?.disconnect();
  });

  async function addNewTab() {
    const tabId = uuidv4();

    tabBlocks.setActiveTab(blockId, tabId, "right");

    const title = `Tab ${tabs.length + 1}`;

    editor.commands.addTabNode({
      pos: getPos(),
      id: tabId,
      title: title,
      blockId: blockId,
    });

    tabs = [...tabs, { id: tabId, title: title }];

    updateAttributes({ tabs });

    // wait for tab to be added before setting as active & scrolling into view
    await tick();

    tabsContainer.lastElementChild?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  async function deleteTab(blockId: string, tabId: string, activeTabIndex: number) {
    // update the tabs array
    tabs = [...tabs.filter((tab: Tab) => tab.id !== tabId)];

    await tick();

    updateAttributes({ tabs });

    // signal the tab node to delete the relevant tab content
    tabBlocks.setTabToDelete(blockId, tabId);

    // if the deleted tab was the last tab, set the previous tab as active, else use the current position
    let newIndex = activeTabIndex === tabs.length ? activeTabIndex - 1 : activeTabIndex;

    tabBlocks.setActiveTab(blockId, tabs[newIndex].id, "left");

    //  check to see if the tabs container is still scrollable after deleting the tab
    // to make sure the scroll buttons are hidden if not needed
    isScrollable = tabsContainer?.scrollWidth > tabsContainer?.clientWidth;
  }

  async function deleteTabBlock() {
    updateAttributes({ tabs: [] });
    await tick();
    tabBlocks.removeTabBlock(blockId);
    deleteNode();
  }

  async function updateTab(title: string, index: number) {
    // update the title of the tab at the active index
    tabs[index].title = title;
    tabs = tabs;

    // wait for DOM to update pending changes before updating node attrs
    await tick();

    updateAttributes({ tabs });
  }
</script>

<NodeViewWrapper class="group relative my-2">
  <div class="flex flex-col rounded-md border border-gray-300 dark:border-gray-600">
    <!-- topbar -->
    <div
      class="flex h-[50px] w-full items-center justify-between rounded-t-md border-b border-gray-300 bg-white px-2 dark:border-gray-600 dark:bg-white/2"
    >
      <!-- scroll left button -->
      <div
        class="clip-left flex h-full items-center shadow-[10px_0_6px_rgb(255,255,255)] transition-[width] dark:shadow-[10px_0_6px_rgb(31,31,35)]"
        style={isScrollable && !atMinScroll ? `width: 28px` : `width: 0`}
      >
        {#if isScrollable && !atMinScroll}
          <div transition:fly={{ x: -5 }}>
            <IconButton
              icon={Left}
              a11yLabel="Scroll left"
              variant="secondary"
              on:click={() =>
                tabsContainer?.scrollTo({
                  left: (scrollPosition -= 150),
                  behavior: "smooth",
                })}
            />
          </div>
        {/if}
      </div>

      <!-- scrollable tabs container -->
      <div
        bind:this={tabsContainer}
        class="no-scrollbar flex w-full gap-1"
        contenteditable={false}
        role="tablist"
        on:scroll={updateScrollStatus}
      >
        <!-- tab buttons -->
        {#each tabs as tab, index (tab.id)}
          <TabButton
            {isEditable}
            title={tab.title}
            tabId={tab.id}
            {blockId}
            on:switch-tab={() => {
              // find index of existing active tab to determine click direction (for animation)
              let activeTabIndex = tabs.findIndex(
                (targetTab) => targetTab.id === $tabBlocks.get(blockId)?.activeTab
              );

              tabBlocks.setActiveTab(blockId, tab.id, index < activeTabIndex ? "left" : "right");
            }}
            on:update-title={(event) => {
              updateTab(event.detail.value, index);
            }}
            on:delete-tab={() => {
              targetTab = { id: tab.id, title: tab.title, index: index };
              isDeleteTabModalOpen = true;
            }}
          />
        {/each}
      </div>

      <div class="flex h-full items-center">
        <!-- scroll right button -->
        <div
          class="clip-right flex h-full items-center shadow-[-10px_0_6px_rgb(255,255,255)] transition-[width] dark:shadow-[-10px_0_6px_rgb(31,31,35)]"
          style={isScrollable && !atMaxScroll ? `width: 28px` : `width: 0`}
        >
          {#if isScrollable && !atMaxScroll}
            <div transition:fly={{ x: 5 }}>
              <IconButton
                icon={Right}
                a11yLabel="Scroll right"
                variant="secondary"
                on:click={() =>
                  tabsContainer?.scrollTo({
                    left: (scrollPosition += 150),
                    behavior: "smooth",
                  })}
              />
            </div>
          {/if}
        </div>

        {#if isEditable}
          <!-- add new tab button -->
          <div class="ml-2 border-l border-gray-300 pl-2 dark:border-gray-600">
            <IconButton
              icon={Plus}
              variant="tertiary"
              a11yLabel="Add new tab"
              on:click={addNewTab}
            />
          </div>
        {/if}
      </div>
    </div>
    <!-- tab content -->
    <NodeViewContent />
  </div>

  {#if isEditable}
    <GutterMenu on:delete-block={() => (isDeleteBlockModalOpen = true)} />
  {/if}

  <!-- modal for single tab delete -->
  <Dialogue
    title="Delete tab"
    message={`Are you sure you want to delete "${targetTab.title}"? All content inside the tab will be permanently removed and cannot be recovered.`}
    bind:open={isDeleteTabModalOpen}
    confirmLabel="Delete forever"
    danger
    on:confirm={() =>
      tabs.length === 1 ? deleteTabBlock() : deleteTab(blockId, targetTab.id, targetTab.index)}
  />

  <!-- modal for tab block node delete -->
  <Dialogue
    title="Delete tabs"
    message="Are you sure you want to delete this block? All content inside the tab will be permanently removed and cannot be recovered."
    bind:open={isDeleteBlockModalOpen}
    confirmLabel="Delete forever"
    danger
    on:confirm={deleteTabBlock}
  />
</NodeViewWrapper>

<style>
  /* clip region to only have shadow on outside of buttons */
  .clip-left {
    clip-path: inset(0px -15px 0px 0px);
    z-index: 1; /* needed to bring the shadow above the tab buttons */
  }

  .clip-right {
    clip-path: inset(0px 0px 0px -15px);
  }
</style>
